import React, { useState, useEffect } from 'react';
import { CheckCircle2, Upload, FileText } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
  Button, ICON_PX,
} from '../ui';

function CompletionModal({ open, onOpenChange, task, onComplete }) {
  const [note, setNote] = useState('');
  const [linkedFiles, setLinkedFiles] = useState([]);
  const [confirmAssignment, setConfirmAssignment] = useState(false);

  useEffect(() => {
    if (open) {
      setNote('');
      setLinkedFiles([]);
      setConfirmAssignment(false);
    }
  }, [open]);

  if (!task) return null;

  const type = task.completionType || 'checkbox';

  const isValid = () => {
    if (type === 'checkbox') return true;
    if (type === 'checkbox_note') return note.length >= 10;
    if (type === 'checkbox_linked') return linkedFiles.length > 0 || confirmAssignment;
    return true;
  };

  const handleSubmit = () => {
    onComplete?.(task.id, {
      note: type === 'checkbox_note' ? note : undefined,
      linkedOutputs: type === 'checkbox_linked' ? linkedFiles : undefined,
    });
    onOpenChange(false);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setLinkedFiles((prev) => [...prev, ...files.map((f) => ({ name: f.name, type: f.type, size: f.size }))]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Complete Task</DialogTitle>
          <DialogDescription>{task.taskName || task.name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-6 py-3">
          {/* Checkbox type — simple confirm */}
          {type === 'checkbox' && (
            <p className="text-body text-text-muted">
              Confirm completion of this task. No additional documentation required.
            </p>
          )}

          {/* Checkbox + Note */}
          {type === 'checkbox_note' && (
            <div>
              <label className="block text-body-sm font-medium text-text-secondary mb-1">
                Completion Note <span className="text-red-500">*</span>
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Describe what was done (minimum 10 characters)..."
                rows={4}
                className="w-full rounded-md border border-nn-border bg-nn-white px-3 py-2 text-body text-text-dark focus:outline-none focus:ring-2 focus:ring-aqua/40 resize-none"
              />
              <div className="flex items-center justify-between mt-1">
                <span className={`text-body-sm ${note.length < 10 ? 'text-red-500' : 'text-text-muted'}`}>
                  {note.length}/10 minimum
                </span>
              </div>
            </div>
          )}

          {/* Checkbox + Linked Output */}
          {type === 'checkbox_linked' && (
            <div className="space-y-3">
              <p className="text-body-sm text-text-muted">
                Provide at least one form of evidence to complete this task.
              </p>

              {/* File upload */}
              <div>
                <label className="block text-body-sm font-medium text-text-secondary mb-1">Upload File(s)</label>
                <label className="flex items-center justify-center gap-2 p-4 rounded-lg border-2 border-dashed border-nn-border hover:border-aqua cursor-pointer transition-colors">
                  <Upload size={ICON_PX.lg} className="text-text-muted" />
                  <span className="text-body-sm text-text-muted">Click or drag to upload</span>
                  <input type="file" multiple className="hidden" onChange={handleFileSelect} />
                </label>
                {linkedFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {linkedFiles.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-body-sm text-text-secondary p-1.5 bg-nn-bg-subtle rounded">
                        <FileText size={ICON_PX.sm} />
                        <span className="truncate">{f.name}</span>
                        <button
                          type="button"
                          onClick={() => setLinkedFiles((prev) => prev.filter((_, idx) => idx !== i))}
                          className="ml-auto text-text-muted hover:text-red-500"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Confirm assignment */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="confirm-assignment"
                  checked={confirmAssignment}
                  onChange={(e) => setConfirmAssignment(e.target.checked)}
                  className="rounded border-nn-border"
                />
                <label htmlFor="confirm-assignment" className="text-body-sm text-text-secondary">
                  Confirm assignment was sent
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="success" size="sm" disabled={!isValid()} onClick={handleSubmit}>
            <CheckCircle2 size={ICON_PX.md} className="mr-1" /> Complete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CompletionModal;
